import { DEMO_TOOLS, dynamicToolFeature, dynamicToolId, type DynamicTool, type Feature } from "@passio/api-client";
import {
  IconBarcode,
  IconBook2,
  IconCalculator,
  IconCamera,
  IconChartDonut,
  IconCalendarEvent,
  IconCalendarStats,
  IconBasket,
  IconChefHat,
  IconFridge,
  IconHeartbeat,
  IconLink,
  IconMenu2,
  IconMessageChatbot,
  IconReplace,
  IconRoute,
  IconSalad,
  IconSearch,
  IconShoppingCart,
  IconSparkles,
  IconStethoscope,
  IconTextRecognition,
  IconToolsKitchen2,
  type Icon,
} from "@tabler/icons-react";
import { useSession } from "./session";

export interface NavItem {
  /** `DEMO_TOOLS` id: admins can switch the page off. */
  tool: string;
  path: string;
  label: string;
  description: string;
  icon: Icon;
  feature: Feature;
  /** Passio route ids the page demonstrates. */
  routes: string[];
}

export interface NavSection {
  verb: string;
  blurb: string;
  items: NavItem[];
}

const tool = (id: string, path: string, description: string, icon: Icon): NavItem => {
  const t = DEMO_TOOLS.find((x) => x.id === id);
  if (!t) throw new Error(`Unknown demo tool ${id}`);
  return { tool: id, path, label: t.label, description, icon, feature: t.feature, routes: t.routes };
};

/** Pages organised by the homepage verbs. */
export const NAV: NavSection[] = [
  {
    verb: "Start here",
    blurb: "Chained calls, start to finish.",
    items: [tool("tour", "/tour", "Text, photo, barcode and recipe flows", IconRoute)],
  },
  {
    verb: "Recognize",
    blurb: "Photo, barcode and text in, structured foods out.",
    items: [
      tool("search", "/search", "Semantic + advanced search", IconSearch),
      tool("barcode", "/barcode", "UPC/EAN lookup", IconBarcode),
      tool("photo", "/photo", "Meals, plates and packaged products", IconCamera),
      tool("text", "/text", "Free text, structured meals, log actions", IconTextRecognition),
      tool("alternatives", "/alternatives", "“Did you mean…?” for unclear scans", IconSparkles),
    ],
  },
  {
    verb: "Understand",
    blurb: "What's in it, and how it behaves.",
    items: [
      tool("insights", "/insights", "Glycemic load, UPF, rate for any goal", IconChartDonut),
      tool("dish", "/dish", "Hidden, possible and predicted", IconToolsKitchen2),
      tool("link", "/link", "Match a structured list", IconLink),
    ],
  },
  {
    verb: "Recommend",
    blurb: "Better choices in the same context.",
    items: [
      tool("advisor", "/advisor", "Ask anything about food", IconMessageChatbot),
      tool("gi_coach", "/gi-coach", "GLP-1 meal and symptom coach", IconStethoscope),
    ],
  },
  {
    verb: "Cook",
    blurb: "From what's in the kitchen to dinner, and what fits your health.",
    items: [
      tool("pantry_recipe", "/cook", "Fridge photo, video, voice or text to a recipe", IconFridge),
      tool("recreate_dish", "/recreate", "A dish you saw to a home recipe", IconChefHat),
      tool("recipe_makeover", "/makeover", "Rework a recipe for a goal", IconSalad),
      tool("substitutes", "/substitutes", "Swaps with exact ratios", IconReplace),
      tool("menu_coach", "/menu-coach", "Best picks from a restaurant menu", IconMenu2),
      tool("condition_check", "/condition-check", "Does a food fit a health condition?", IconHeartbeat),
      tool("daily_review", "/daily-review", "A day's log vs. targets, and what to eat next", IconCalendarStats),
    ],
  },
  {
    verb: "Plan & shop",
    blurb: "From intent to a cart.",
    items: [
      tool("meal_plans", "/meal-plans", "Fast, family and more", IconCalendarEvent),
      tool("grocery_swaps", "/grocery-swaps", "Keto, less processed picks", IconBasket),
      tool("shopping", "/shopping", "Shopper units, then an Instacart link", IconShoppingCart),
    ],
  },
  {
    verb: "Reference",
    blurb: "Catalogs behind the filters, and the math on top.",
    items: [
      tool("nutrition_math", "/nutrition-math", "Portions, nutrients, totals, with code", IconCalculator),
      tool("reference", "/reference", "Tags, concerns, scan defaults", IconBook2),
    ],
  },
];

export const ALL_NAV_ITEMS = NAV.flatMap((s) => s.items);

/** Dynamic AI tools published from EasyAI, one page each at /tools/<slug>. */
export function dynamicNavSection(tools: DynamicTool[]): NavSection {
  return {
    verb: "New AI tools",
    blurb: "Fresh from Easy AI Studio.",
    items: tools.map((t) => ({
      tool: dynamicToolId(t.slug),
      path: `/tools/${t.slug}`,
      label: t.status === "testing" ? `${t.summary} (testing)` : t.summary,
      description: t.description.length > 60 ? `${t.description.slice(0, 57)}…` : t.description,
      icon: IconSparkles,
      feature: dynamicToolFeature(t),
      routes: [],
    })),
  };
}

/** NAV without pages an admin switched off (empty sections dropped), plus the dynamic tools. */
export function useNav(): NavSection[] {
  const { toolEnabled, me } = useSession();
  return [...NAV, dynamicNavSection(me?.app.dynamicTools ?? [])]
    .map((s) => ({ ...s, items: s.items.filter((i) => toolEnabled(i.tool)) }))
    .filter((s) => s.items.length > 0);
}
